import './styles.scss';

import { faExclamationCircle, faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, CircularProgress, Modal } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Input from '../../../../components/input/input';
import { VenueContext } from '../../../../providers/venue-provider';
import Utils from '../../../../utils/immersive_utils';

interface AccountType {
  name: string;
  label: string;
  icon: string;
}

interface IProps {
  open: boolean;
  handleClose: () => void;
  handleCreate: () => void;
  accountType?: AccountType;
}

type Inputs = {
  key: string;
  secret: string;
  name: string;
  passphrase: string;
  testnet: boolean;
};

const NewAccountModal = ({ open, handleClose, accountType, handleCreate }: IProps) => {
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    watch,
    formState: { errors, isValid },
  } = useForm<Inputs>();

  const key = watch('key');
  const secret = watch('secret');
  const passphrase = watch('passphrase');

  const {
    saveAccount,
    isLoading,
    isSuccess,
    hasErrors,
    errorDetail,
    refreshAccounts,
    setIsSuccess,
  } = useContext(VenueContext);

  const [currentAccountType, setCurrentAccountType] = useState<AccountType | undefined>(undefined);

  const onSubmit = (data: Inputs) => {
    const { name, secret, key, passphrase, testnet } = data;

    saveAccount({ name, secret, key, passphrase, testnet }, accountType?.name.toLowerCase() || '');
    clearErrors();
  };

  useEffect(() => {
    if (hasErrors) {
      clearErrors();
    }
  }, [key, secret, passphrase]);

  useEffect(() => {
    if (open) {
      if (currentAccountType && currentAccountType.name !== accountType?.name) {
        reset();
      }
      setCurrentAccountType(accountType);
    }
  }, [open, reset]);

  useEffect(() => {
    if (hasErrors && !isLoading) {
      setError('key', { type: 'empty', message: '' });
      setError('secret', { type: 'empty', message: '' });
      setError('passphrase', { type: 'empty', message: '' });

      return;
    }

    if (isSuccess && !isLoading) {
      setIsSuccess(false);
      reset();
      clearErrors();
      handleCreate();
      refreshAccounts();
    }
  }, [handleCreate, hasErrors, isLoading, isSuccess, refreshAccounts, reset]);

  const wrapperHandleClose = () => {
    reset();
    clearErrors();
    handleClose();
  };

  const handleSelectBinanceTestNet = () => {
    clearErrors();
  };

  return (
    <Modal open={open} onClose={wrapperHandleClose}>
      <div className="new-account-modal-container">
        <div className="new-account-modal-content">
          <div className="header">
            <h1>Connect with your {accountType?.label} account</h1>
            <Button className="close-button" onClick={wrapperHandleClose}>
              <FontAwesomeIcon icon={faX} />
            </Button>
          </div>

          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="API Key*"
              placeholder="Enter your API Key"
              {...register('key', { required: true })}
              errors={errors?.key?.type}
              {...Utils.obscuredProps}
            />
            <Input
              label="API Secret*"
              placeholder="Enter your API Secret"
              type="password"
              {...register('secret', { required: true })}
              errors={errors?.secret?.type}
              {...Utils.obscuredProps}
            />
            {accountType?.name === 'okx' && (
              <Input
                label="Passphrase*"
                placeholder="Enter your API Passphrase"
                type="passphrase"
                {...register('passphrase', { required: true })}
                errors={errors?.passphrase?.type}
                {...Utils.obscuredProps}
              />
            )}
            <Input
              label="API Key Name"
              placeholder="Create a name for this key (optional)"
              {...register('name')}
              errors={errors?.name?.type}
            />

            {accountType?.name === 'binance' && (
              <div>
                <Checkbox {...register('testnet')} onChange={handleSelectBinanceTestNet} />
                <span>Testnet</span>
              </div>
            )}

            {!!errors?.key && (
              <div className="general-errors">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>
                  {errorDetail || 'Not a valid connection. Please review your API Key credentials'}
                </span>
              </div>
            )}

            <Button type="submit" className="button" disabled={!isValid || isLoading}>
              {isLoading ? <CircularProgress size={24} /> : <span>Connect</span>}
            </Button>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default NewAccountModal;
